/**
 * Pure timing / attribution logic behind TransmissionMeasurements.tsx.
 *
 * Split out of the component for ONE reason: this file has no imports and no JSX, so a
 * test can transpile it with esbuild and EXECUTE it under node. That matters more here
 * than anywhere else in the frontend, because the invariant being protected is a
 * negative one — "no measurement ever renders as NaN, undefined, Infinity, or a
 * fabricated 0" — and the only convincing way to show that is to run every formatter
 * against an all-null row and look at what comes out. A structural test can prove a
 * guard clause is written; it cannot prove the arithmetic never escapes it.
 *
 * Keep this file free of imports. Adding one turns the executable test into a bundling
 * exercise and the guard will be silently downgraded to a skip.
 */

/** What the panel reads. Every member is optional AND nullable — which is what the API
 *  type declares, and what the endpoints actually send today: null, for all of them. */
export interface TransmissionMeasurementSource {
  // Outbound leg. Shown elsewhere too, but needed as the timeline's origin.
  t_submitted?: string | null
  t_fax_sent?: string | null

  // The four inbound clocks. Four DIFFERENT facts; conflating any two is the mistake
  // this panel exists to prevent.
  /** eFax's clock: when eFax says the fax finished. */
  t_efax_completed?: string | null
  /** eFax's clock: when eFax emitted the notification. */
  t_efax_notified?: string | null
  /** Our clock: when the notification actually landed on us. */
  t_notified?: string | null
  /** Best estimate of ACTUAL delivery — deliberately NOT normalised to our poll clock. */
  t_received?: string | null

  /** eFax's own transmission airtime, in seconds. May be fractional. */
  fax_duration_seconds?: number | null
  /** Pages put on the wire for THIS transmission, not the job's current document. */
  sent_page_count?: number | null

  // Pod attribution, parsed from J2MsgID. Outbound = "CCS pod", inbound = "AFC pod".
  outbound_pod_name?: string | null
  outbound_pod_region?: string | null
  outbound_pod_env?: string | null
  outbound_msg_id?: string | null
  inbound_pod_name?: string | null
  inbound_pod_region?: string | null
  inbound_pod_env?: string | null
  inbound_msg_id?: string | null

  // Transport evidence. `inbound_method` is authoritative when present; the payload
  // fields are the fallback for endpoints that do not return it.
  inbound_method?: string | null
  imap_message_id?: string | null
  imap_headers?: Record<string, string> | null
  webhook_payload_body?: Record<string, unknown> | null
  webhook_payload_headers?: Record<string, string> | null

  // ---- provenance (G5) -----------------------------------------------------
  // The remaining timestamps a detail page shows. Declared here so ONE module can
  // describe every timestamp on both pages; the panel itself does not render these.
  t_scheduled?: string | null
  t_quality_checked?: string | null
  /**
   * Our own pipeline phases — rung 0, so none of these ever reaches an SLA leg. They exist so
   * the timeline can show where OUR time went, which is what separates "the provider was slow"
   * from "we were". The OCR pair includes the CPU-gate wait.
   */
  t_ocr_started?: string | null
  t_ocr_finished?: string | null
  t_quality_started?: string | null
  manually_failed_at?: string | null
  smtp_notification_received_at?: string | null
  /** Taxonomy event 1: when OUR outbound request started. NULL is not zero latency. */
  t_outbound_request_started?: string | null
  /**
   * When the PROVIDER said the call was on the line. NULL means it never said, which is the
   * common case rather than the exception: only eFax reports it, and no SMTP gateway reports
   * anything at all between accepting the mail and delivering a notification.
   */
  t_line_started?: string | null

  /** Which source actually produced `t_received` on THIS row. One of two stored codes. */
  t_received_source?: string | null
  /** Which source actually produced `t_fax_sent` on THIS row. */
  t_fax_sent_source?: string | null

  /**
   * THE SECOND SOURCE for the outbound completion — recorded, DISPLAYED, and never scored (W5).
   *
   * The completion implied by the provider's OWN reported duration: SFax's `Time + Duration` off
   * its confirmation email. It exists because `t_fax_sent` on that provider is `faxdateiso`,
   * which is its ACCEPTANCE — on one real run, eleven seconds after we submitted a fax that
   * takes about fifty seconds to transmit, so the leg reads 11 s for about 62 s of transmission.
   *
   * It is a SEPARATE member and not a replacement, deliberately (Decision 1, settled
   * 2026-09-12). Overwriting the scored value would publish a new number on one run's arithmetic
   * and hide the gap; carrying both means the discrepancy is visible on every such fax, and its
   * size and consistency can be judged from real traffic before anyone trusts it.
   */
  t_fax_sent_derived?: string | null
  /** WHICH derivation produced `t_fax_sent_derived` — a stored provenance code, not a guess. */
  t_fax_sent_derived_source?: string | null

  // Stored provider and transport per LEG, so provenance is a lookup rather than a guess.
  outbound_provider?: string | null
  inbound_provider?: string | null
  outbound_transport?: string | null
  inbound_transport?: string | null

  /** Resolved provenance per timestamp column, straight from the backend registry. */
  timestamp_provenance?: Record<string, TimestampProvenance> | null
}

// ==========================================================================
// PROVENANCE — where each timestamp came from (G5, G10.7)
//
// This mirrors `backend/provenance.py`, which is the shared contract. The rung
// VALUES and the rung LABELS below are the same words the backend uses, and a test
// reads both files and fails when they drift — the point of the ladder is that a
// degraded measurement is visibly degraded, and two surfaces disagreeing about what
// rung 2 means destroys that.
//
// It lives in this file rather than a new one for the same reason the arithmetic does:
// no imports, no JSX, so a test can transpile it and EXECUTE describeTimestamp against
// a real row. "The description reflects the stored provenance for THAT ROW" is a claim
// about output, and only running it can establish that.
// ==========================================================================

/** The authority ladder, best first. 0 is off the ladder: our own pipeline. */
export type Rung = 0 | 1 | 2 | 3

export const RUNG_OUR_INTERNAL_PHASE: Rung = 0
export const RUNG_PROVIDER_OF_THIS_LEG: Rung = 1
export const RUNG_DERIVED_FROM_LATER_HEADER: Rung = 2
export const RUNG_OUR_EDGE_OBSERVATION: Rung = 3

/** MUST equal backend provenance.RUNG_LABELS. Pinned by a test that reads both files. */
export const RUNG_LABELS: Record<string, string> = {
  '0': 'our own pipeline',
  '1': 'the provider for this leg',
  '2': 'derived from a header received later',
  '3': 'our own observation at arrival',
}

/** One resolved origin, as the API projects it. Mirrors backend `Source`. */
export interface TimestampProvenance {
  code?: string | null
  rung?: Rung | null
  event?: string | null
  field?: string | null
  transport?: string | null
  caveat?: string | null
  /**
   * The poll interval in seconds that BOUNDED this reading, when one did (B31).
   *
   * Present only where the timestamp came from our own observation: a poll proves the event
   * happened at some point BEFORE we looked, so this number is the width of the reading's
   * uncertainty. Absent means NOT BOUNDED BY US — a provider-reported time — not "bounded by
   * an unknown amount", which is why it is optional rather than nullable-with-a-default.
   *
   * It travels with the row instead of being read from the account's current setting, because
   * the cadence is configurable: today's number is not necessarily the one that applied when
   * this fax was measured.
   */
  bound_by_poll_interval_seconds?: number | null
}

/** Which side of the measurement a column belongs to. 'internal' is never an SLA input. */
export type TimestampLeg = 'outbound' | 'inbound' | 'internal'

export interface TimestampMeta {
  /** THE label. One per column, used by run detail AND batch item detail (G5.15). */
  label: string
  /** The event in plain words. Sentence one of every description. */
  event: string
  leg: TimestampLeg
  /**
   * For an internal phase: the concrete clock, stated as fact. There is exactly one
   * possible source for our own pipeline, so this is never an inference (G5.8).
   */
  ourClock?: string
  /** Transport-implied likely source, for a row whose provenance was never recorded. */
  likely?: Record<string, string>
  /** Used when the row does not even record which transport carried the leg. */
  likelyUnknown?: string
}

/**
 * Every timestamp column either detail page can show, with its one label.
 *
 * G5.17 requires coverage for all of these, and a test enumerates the timestamp columns
 * the models declare and fails when one is missing here — so a new timestamp cannot be
 * added without a description.
 */
export const TIMESTAMP_CATALOG: Record<string, TimestampMeta> = {
  // ---- our own pipeline. Rung 0, off the ladder, never an SLA input --------
  t_scheduled: {
    label: 'Scheduled',
    event: 'Our scheduler queued this run for sending.',
    leg: 'internal',
    ourClock: 'our own scheduler clock when the run was queued',
  },
  t_outbound_request_started: {
    label: 'Send request started (ours)',
    event: 'We began the outbound send request to the provider.',
    leg: 'internal',
    ourClock:
      'our own clock immediately before the outbound call — on SMTP that is before ' +
      'connection establishment, so TLS negotiation is inside the window, and on a ' +
      'retried send the FIRST attempt is kept',
  },
  t_line_started: {
    label: 'On the line (provider)',
    event: 'The provider reported the outbound call as in progress.',
    leg: 'outbound',
    likely: {
      api:
        "the provider's own report that the fax is on the wire, as distinct from accepted " +
        'and queued — recorded only by a provider that publishes the difference',
    },
    likelyUnknown:
      'absent on every leg whose provider does not distinguish dialling from queued, which ' +
      'is most of them, and never substituted from our own clock: a stand-in here would ' +
      'shorten the apparent outbound leg and make the provider look faster than it was',
  },
  t_quality_checked: {
    label: 'Quality checked',
    event: 'Our quality worker finished checking the returned fax.',
    leg: 'internal',
    ourClock: 'our own clock when the check completed',
  },
  t_ocr_started: {
    label: 'OCR started (ours)',
    event: 'Our OCR worker began reading the received fax.',
    leg: 'internal',
    ourClock:
      'our own clock when the read began, INCLUDING the wait for a CPU slot — that wait ' +
      'is our own backlog, and a number that excluded it would hide the thing this ' +
      'timestamp exists to show',
  },
  t_ocr_finished: {
    label: 'OCR finished (ours)',
    event: 'Our OCR worker returned the text it read.',
    leg: 'internal',
    ourClock: 'our own clock when the read returned',
  },
  t_quality_started: {
    label: 'Quality started (ours)',
    event: 'Our quality scorer began measuring the received fax.',
    leg: 'internal',
    ourClock:
      'our own clock when scoring began, taken after the item left the scoring queue so ' +
      'a busy period reads as queue depth rather than as slow scoring',
  },
  manually_failed_at: {
    label: 'Manually failed',
    event: 'An administrator marked this run failed.',
    leg: 'internal',
    ourClock: 'our own API clock when the resolution was recorded',
  },
  t_created: {
    label: 'Batch created',
    event: 'The batch ledger was written.',
    leg: 'internal',
    ourClock: 'our own clock when the batch rows were committed',
  },
  t_dispatch_started: {
    label: 'Dispatch started',
    event: 'The leader worker began dispatching this batch.',
    leg: 'internal',
    ourClock: 'our own clock on the dispatching worker',
  },
  t_dispatch_complete: {
    label: 'Dispatch complete',
    event: 'Every item in the batch had been handed to a provider.',
    leg: 'internal',
    ourClock: 'our own clock on the dispatching worker',
  },
  t_completed: {
    label: 'Batch complete',
    event: 'Every item in the batch reached a terminal state.',
    leg: 'internal',
    ourClock: 'our own clock when the last item settled',
  },

  // ---- outbound leg -------------------------------------------------------
  t_submitted: {
    label: 'Submitted (provider accepted)',
    event: 'The provider accepted the fax for transmission.',
    leg: 'outbound',
    likely: {
      api: "our own clock when the provider's accept response returned",
      webhook: "our own clock when the provider's accept response returned",
      smtp: 'our own clock when the SMTP server accepted the message',
      imap: 'our own clock when the send was accepted',
    },
    likelyUnknown: 'our own clock at the moment of acceptance',
  },
  // NOT "Fax Sent (eFax completed)". That named an eFax clock for a value that can be
  // OURS: both write sites read `_parse(...) or datetime.now(timezone.utc)`, so a
  // provider-reported completion and our own fallback were indistinguishable on screen.
  t_fax_sent: {
    label: 'Fax sent (outbound complete)',
    event: 'The outbound leg finished and the transmission was reported complete.',
    leg: 'outbound',
    likely: {
      api: "the provider's reported completion time on the status poll, or OUR OWN clock " +
        'when we observed completion if the provider reported none',
      webhook: "the provider's reported completion time on the notification, or OUR OWN " +
        'clock when the notification landed if the provider reported none',
      smtp: "the provider's completion time on the confirmation message, or OUR OWN clock " +
        'when we observed it',
      imap: "the provider's completion time on the confirmation message, or OUR OWN clock " +
        'when we observed it',
    },
    likelyUnknown:
      "either the provider's reported completion or OUR OWN observation clock, and which " +
      'of the two it was is exactly what went unrecorded',
  },

  // THE SECOND SOURCE for the same event, kept as its own described column (W5). It is NOT a
  // variant label on `t_fax_sent`: the two are different readings and the operator is meant to
  // see both, which is impossible if one of them has no description of its own.
  t_fax_sent_derived: {
    label: 'Fax sent (derived from reported duration)',
    event:
      "The completion implied by the provider's own reported transmission duration — a SECOND " +
      'SOURCE, which is recorded and shown but never scored.',
    leg: 'outbound',
    likely: {
      api:
        "the provider's reported acceptance time plus its own reported duration, taken from " +
        'its confirmation message — OUR arithmetic on two of its fields, not an instant it ' +
        'stated',
      webhook:
        "the provider's reported acceptance time plus its own reported duration, taken from " +
        'its confirmation message — OUR arithmetic on two of its fields, not an instant it ' +
        'stated',
      smtp:
        "the provider's reported acceptance time plus its own reported duration on the " +
        'confirmation message — OUR arithmetic on two of its fields',
      imap:
        "the provider's reported acceptance time plus its own reported duration on the " +
        'confirmation message — OUR arithmetic on two of its fields',
    },
    likelyUnknown:
      "the provider's own acceptance time plus its own reported duration, added by us — and " +
      'which fields those were is exactly what went unrecorded',
  },

  // ---- inbound leg --------------------------------------------------------
  t_received: {
    label: 'Received (inbound delivery)',
    event: 'The returned fax was delivered back to us.',
    leg: 'inbound',
    likely: {
      imap: 'the topmost `Received:` header of the inbound email (delivery to the MX), ' +
        'falling back to its `Date:` header and then to our own poll clock',
      webhook: "the provider's `notificationTimestamp` in the webhook payload, falling back " +
        'to our own clock at the webhook landing',
      api: "the provider's reported delivery time",
      smtp: 'the topmost `Received:` header of the inbound email',
    },
    likelyUnknown:
      'a provider timestamp, an email header, or our own arrival clock, and which of the ' +
      'three it was is exactly what went unrecorded',
  },
  t_notified: {
    label: 'Notification landed',
    event: 'The notification about the returned fax reached our edge.',
    leg: 'inbound',
    likely: {
      webhook: 'our own clock when the webhook landed at our edge',
      imap: 'our own clock when the poller fetched the message',
      api: 'our own clock when we learned of the notification',
      smtp: 'our own clock when the message was fetched',
    },
    likelyUnknown: 'our own clock when we learned of the notification',
  },
  t_efax_completed: {
    label: 'Provider reported completion',
    event: 'The provider says the fax itself finished.',
    leg: 'inbound',
    likely: {
      webhook: "the provider's own completion field on the notification payload",
      imap: 'the `X-J2FaxDateTime` header of the inbound email',
      api: "the provider's own completion field on the status response",
      smtp: 'the `X-J2FaxDateTime` header of the confirmation message',
    },
    likelyUnknown: "the provider's own reported completion field",
  },
  t_efax_notified: {
    label: 'Provider emitted notification',
    event: 'The provider emitted its notification about the finished fax.',
    leg: 'inbound',
    likely: {
      webhook: "the provider's `notificationTimestamp` in the payload",
      imap: 'the `Date:` header the provider set on the notification email',
      api: "the provider's notification timestamp field",
      smtp: 'the `Date:` header of the confirmation message',
    },
    likelyUnknown: "the provider's own notification timestamp",
  },
  smtp_notification_received_at: {
    label: 'SMTP notification received',
    event: "The provider's SMTP confirmation message arrived.",
    leg: 'inbound',
    likely: {
      smtp: 'our own clock when the confirmation message was fetched',
      imap: 'our own clock when the confirmation message was fetched',
      webhook: 'our own clock when the confirmation message was fetched',
      api: 'our own clock when the confirmation message was fetched',
    },
    likelyUnknown: 'our own clock when the confirmation message was fetched',
  },
}

/** THE label for each column — the one both detail pages must use (G5.15). */
export const TIMESTAMP_LABELS: Record<string, string> = (() => {
  const out: Record<string, string> = {}
  for (const key of Object.keys(TIMESTAMP_CATALOG)) out[key] = TIMESTAMP_CATALOG[key].label
  return out
})()

/**
 * The label for a column, or the column name itself when it is unknown.
 *
 * Returning the raw column rather than throwing is deliberate: an unlabelled timestamp
 * must be VISIBLE on screen (and caught by the coverage test), not crash the page.
 */
export function timestampLabel(column: string): string {
  return TIMESTAMP_CATALOG[column]?.label ?? column
}

/**
 * Transport slugs normalised to the four the contract names.
 *
 * Two vocabularies already exist in the codebase — the stored leg transport
 * ('api' / 'smtp' / 'webhook' / 'imap') and the frontend's older inferred
 * 'eapi_webhook' — and a description that silently falls through to "transport unknown"
 * because of a slug spelling would understate what we actually know about the row.
 */
export function normalizeTransport(transport?: string | null): string | null {
  const t = (transport ?? '').trim().toLowerCase()
  if (!t) return null
  if (t === 'eapi_webhook' || t === 'webhook') return 'webhook'
  if (t === 'eapi' || t === 'api' || t === 'eapi_api') return 'api'
  if (t === 'smtp') return 'smtp'
  if (t === 'imap') return 'imap'
  return t
}

/**
 * The transport that carried the leg this column belongs to, or null.
 *
 * An internal phase deliberately returns null: our own pipeline is not carried by a
 * transport, and borrowing the outbound leg's slug to fill the sentence would attribute
 * our cost to a provider path.
 */
export function transportForColumn(
  column: string,
  src: TransmissionMeasurementSource,
): string | null {
  const meta = TIMESTAMP_CATALOG[column]
  if (!meta || meta.leg === 'internal') return null
  if (meta.leg === 'outbound') return normalizeTransport(src.outbound_transport)
  const stored = normalizeTransport(src.inbound_transport)
  if (stored) return stored
  // Fall back to the older evidence-based inference rather than reporting "unknown"
  // for a row written before the transport column existed.
  const inferred = inferInboundTransport(src)
  return inferred === 'unknown' ? null : normalizeTransport(inferred)
}

/** The stored provenance CODE for a column, for the three columns that record one. */
export function storedProvenanceCode(
  column: string,
  src: TransmissionMeasurementSource,
): string | null {
  if (column === 't_received') return src.t_received_source || null
  if (column === 't_fax_sent') return src.t_fax_sent_source || null
  // W5 — the derived completion records WHICH arithmetic produced it, for the same reason the
  // two above record which branch ran: more than one provider's fields can produce this value.
  if (column === 't_fax_sent_derived') return src.t_fax_sent_derived_source || null
  return null
}

export interface TimestampDescription {
  column: string
  /** THE label for this column. */
  label: string
  /** At most two sentences: the event, the source, the transport, and any fallback. */
  text: string
  rung: Rung | null
  /** True when nothing was recorded and the named source is a transport-implied guess. */
  inferred: boolean
  /** True for a rung-0 phase: our own cost, never an SLA input. */
  internal: boolean
  /** True when the recorded rung is worse than the provider of this leg. */
  degraded: boolean
  /**
   * WHERE THIS VALUE CAME FROM, in two or three words — ALWAYS non-empty.
   *
   * The operator's ruling on the surfacing of provenance, verbatim: *"I dont think we need to
   * display a WARNING, but we should be clear about where the authoritative timestamp came from."*
   * Before this, a timestamp whose recorded rung was worse than the provider of its leg was
   * rendered as an amber CAUTION and a rung-1 one carried no marker at all — so the surface said
   * "be careful" where the operator wants it to say "this came from X".
   *
   * Derived from the same `rung` the sentence in `text` states, so the compact form and the long
   * form cannot disagree; `inferred` is its own value because "we did not record the source" is a
   * different fact from any source.
   */
  sourceTag: string
  /**
   * The poll interval in seconds that bounded this reading, or null when none did (B31).
   *
   * Surfaced BESIDE the timestamp deliberately: two legs measured under different intervals are
   * two different instruments, and a reader comparing them without knowing that is being misled.
   */
  boundByPollIntervalSeconds: number | null
}

/**
 * The compact source name for a rung, and for the absence of a recorded rung.
 *
 * Two or three words, no adjective and no severity — the whole point is that a reader learns WHICH
 * source without being told how to feel about it. The long form stays in `RUNG_LABELS`, which this
 * must not contradict: both are keyed on the same rung, and a test asserts an entry here for every
 * member of `RUNG_LABELS`.
 */
export const SOURCE_TAGS: Record<string, string> = {
  '0': 'ours',
  '1': 'provider',
  '2': 'later header',
  '3': 'our arrival clock',
}

/** `sourceTag` for a described row: the rung's own name, or the absence of a recorded one. */
export function sourceTagFor(rung: Rung | null, inferred: boolean): string {
  if (rung === null) return inferred ? 'source not recorded' : 'source undescribed'
  return SOURCE_TAGS[String(rung)] ?? 'unrecognised source'
}

function transportPhrase(transport: string | null): string {
  return transport ? ` on the ${transport} transport` : ''
}

/**
 * Registry prose folded into ONE sentence.
 *
 * G5.11 caps a description at two sentences, and the `event` and `caveat` text comes from
 * the backend registry — which a sibling lane fills, and which could reasonably hold two
 * sentences of caveat. Collapsing internal sentence breaks to semicolons keeps the cap a
 * property of this function rather than a convention every registry entry has to remember,
 * so a wordy caveat degrades to a long sentence instead of silently breaking the rule.
 *
 * A folded clause that began a sentence also began with a capital, which reads as a typo
 * mid-sentence. Only a CLOSED LIST of ordinary sentence openers is lowercased: guessing
 * from the shape of the word would mangle the identifiers and acronyms these strings are
 * full of — `Received:`, `X-J2FaxDateTime`, SMTP, OCR.
 */
const SENTENCE_OPENERS = [
  'The', 'This', 'That', 'These', 'Those', 'It', 'Its', 'A', 'An', 'We', 'Our', 'They',
  'There', 'Ideally',
]

function oneSentence(text: string): string {
  const folded = text.trim().replace(/\.\s+/g, '; ').replace(/\.$/, '')
  return folded.replace(/; ([A-Z][a-z]*)/g, (whole, word: string) =>
    SENTENCE_OPENERS.indexOf(word) === -1 ? whole : `; ${word.toLowerCase()}`,
  )
}

/**
 * What to tell an operator about ONE timestamp on ONE row.
 *
 * Order of authority, and the whole point of the function:
 *
 *   1. The provenance the backend RESOLVED for this row — the registry entry keyed on
 *      the stored code, or on (provider, transport, column). A fact.
 *   2. An internal phase with no resolved entry — still a fact, because our own pipeline
 *      has exactly one possible source and saying so costs a constant.
 *   3. Nothing recorded — the transport-implied likely source, EXPLICITLY LABELLED as an
 *      inference (G5.13). This is a historical row, and pretending otherwise would let a
 *      guess be read as a record.
 */
export function describeTimestamp(
  column: string,
  src: TransmissionMeasurementSource,
): TimestampDescription {
  const meta = TIMESTAMP_CATALOG[column]
  const label = timestampLabel(column)
  const transport = transportForColumn(column, src)

  if (!meta) {
    return {
      column,
      label,
      text:
        `No description is registered for ${column}. That is a gap in the provenance ` +
        'catalogue, not a fact about this row — treat the value as unexplained.',
      rung: null,
      inferred: true,
      internal: false,
      degraded: false,
      sourceTag: sourceTagFor(null, true),
      boundByPollIntervalSeconds: null,
    }
  }

  const resolved = src.timestamp_provenance?.[column]
  if (resolved && (resolved.event || resolved.field || resolved.rung !== undefined)) {
    const rung = (resolved.rung ?? null) as Rung | null
    const source = resolved.field
      ? `the provider's \`${resolved.field}\``
      : 'our own clock'
    const rungText = rung === null ? 'an unrecorded rung' : RUNG_LABELS[String(rung)] ?? 'an unknown rung'
    const degraded = rung !== null && rung > RUNG_PROVIDER_OF_THIS_LEG
    const caveat = resolved.caveat
      ? `, a FALLBACK rather than the ideal source: ${oneSentence(resolved.caveat)}`
      : ''
    const where = normalizeTransport(resolved.transport) ?? transport
    // B31 — THE INTERVAL THAT BOUNDED THIS READING, in the SAME sentence rather than a third
    // one, because G5.11 caps a description at two. It belongs in the source clause: a poll
    // proves the event happened at some point BEFORE we looked, so the interval is not extra
    // colour about the reading, it is the reading's width.
    const bounding = resolved.bound_by_poll_interval_seconds ?? null
    const boundedPhrase = bounding
      ? `, bounded by this account's ${bounding}s status poll so it may read up to that much late`
      : ''
    return {
      column,
      label,
      text:
        `${oneSentence(resolved.event || meta.event)}. Recorded from ${source}` +
        `${transportPhrase(where)}${boundedPhrase} — ${rungText}${caveat}.`,
      rung,
      inferred: false,
      internal: rung === RUNG_OUR_INTERNAL_PHASE,
      degraded,
      sourceTag: sourceTagFor(rung, false),
      boundByPollIntervalSeconds: bounding,
    }
  }

  if (meta.leg === 'internal') {
    // One possible source, so this is a statement of fact even with an empty registry.
    return {
      column,
      label,
      text:
        `${oneSentence(meta.event)}. Recorded from ${meta.ourClock} — ${RUNG_LABELS['0']}, ` +
        'which is our own cost and never an input to an SLA verdict.',
      rung: RUNG_OUR_INTERNAL_PHASE,
      inferred: false,
      internal: true,
      degraded: false,
      sourceTag: sourceTagFor(RUNG_OUR_INTERNAL_PHASE, false),
      boundByPollIntervalSeconds: null,
    }
  }

  const storedCode = storedProvenanceCode(column, src)
  if (storedCode) {
    // The row RECORDED which branch ran, but the registry has no entry for that code —
    // so the code is reported verbatim rather than being translated into a guess.
    return {
      column,
      label,
      text:
        `${oneSentence(meta.event)}. This row records its source as \`${storedCode}\`` +
        `${transportPhrase(transport)}, but no description is registered for that code, so ` +
        'the rung cannot be stated.',
      rung: null,
      inferred: false,
      internal: false,
      degraded: false,
      sourceTag: sourceTagFor(null, false),
      boundByPollIntervalSeconds: null,
    }
  }

  const likely = (transport && meta.likely?.[transport]) || meta.likelyUnknown
  // The closing clause must not credit a transport the row does not record. "An inference
  // from the transport" on a row with no transport is a sentence that contradicts the
  // clause before it, and an operator reading it cannot tell which half to believe.
  const basis = transport
    ? `on the ${transport} transport it is most likely ${likely} — an INFERENCE from the ` +
      'transport, not a recorded fact'
    : `the row records no transport for this leg either, so it is most likely ${likely} — ` +
      'an INFERENCE, not a recorded fact'
  return {
    column,
    label,
    text: `${oneSentence(meta.event)}. The source was NOT recorded for this row: ${basis}.`,
    rung: null,
    inferred: true,
    internal: false,
    degraded: false,
    sourceTag: sourceTagFor(null, true),
    boundByPollIntervalSeconds: null,
  }
}

// ------------------------------------------------------- G10: our internal phases

/**
 * POST latency: how long the provider took to ACCEPT our send.
 *
 * `t_submitted − t_outbound_request_started`. Null when either end is missing, and a
 * missing start is the common case on a historical row — NULL IS NOT ZERO LATENCY, so
 * this must never be coerced to 0 for display.
 */
export function postLatencySeconds(src: TransmissionMeasurementSource): number | null {
  return intervalSeconds(src.t_outbound_request_started, src.t_submitted)
}

/**
 * Our OWN pipeline phases, kept out of `timelineSegments` on purpose.
 *
 * G10.4 forbids blending this into a provider leg, and the safest way to obey that is
 * structural: an internal phase is never a member of the provider timeline's milestone
 * list, so no adjacent-milestone pairing can ever fold our cost into a leg's segment.
 * The panel renders these in their own block, adjacent to the bar and labelled as ours.
 */
export interface InternalPhase {
  key: string
  label: string
  fromKey: string
  toKey: string
  seconds: number
  hint: string
}

export function internalPhases(src: TransmissionMeasurementSource): InternalPhase[] {
  const phases: InternalPhase[] = []
  const post = postLatencySeconds(src)
  if (post !== null) {
    phases.push({
      key: 'post_latency',
      label: 'Our POST latency (time to accept)',
      fromKey: 't_outbound_request_started',
      toKey: 't_submitted',
      seconds: post,
      hint:
        't_submitted − t_outbound_request_started: how long the provider took to accept ' +
        'our send. OUR OWN pipeline cost, measured with our own clock at the call site. ' +
        'The outbound SLA starts at acceptance, so this never enters a verdict. On a ' +
        'retried send it is the total time to accept across attempts.',
    })
  }
  // OUR OWN DURATIONS, and every one of them is rung 0. They are built HERE rather than in
  // `MILESTONE_ORDER` for the structural reason that list documents: a value absent from the
  // milestone list cannot be paired with an adjacent milestone by `timelineSegments`, so it can
  // never be folded into a leg. Keeping them out of the list legs are computed from is what
  // enforces that, rather than everyone remembering to.
  const ocr = intervalSeconds(src.t_ocr_started, src.t_ocr_finished)
  if (ocr !== null) {
    phases.push({
      key: 'ocr_duration',
      label: 'Our OCR read',
      fromKey: 't_ocr_started',
      toKey: 't_ocr_finished',
      seconds: ocr,
      hint:
        't_ocr_finished − t_ocr_started: how long OUR OCR worker took to read the received ' +
        'fax, INCLUDING its wait for a CPU slot. That wait is our own backlog, so hiding it ' +
        'inside a shorter number would conceal the thing this measurement exists to show. ' +
        'Never enters a verdict: the inbound SLA ends when the fax arrived, not when we read it.',
    })
  }

  const quality = intervalSeconds(src.t_quality_started, src.t_quality_checked)
  if (quality !== null) {
    phases.push({
      key: 'quality_duration',
      label: 'Our quality scoring',
      fromKey: 't_quality_started',
      toKey: 't_quality_checked',
      seconds: quality,
      hint:
        't_quality_checked − t_quality_started: how long OUR scorer took to measure the fax. ' +
        'Until the start existed this was unanswerable — the finish time had nothing to ' +
        'subtract from. Measured after the item leaves the scoring queue, so a busy period ' +
        'reads as queue depth rather than as slow scoring, and never enters a verdict.',
    })
  }

  return phases
}

// -------------------------------------------- W5: the outbound leg's SECOND SOURCE

/**
 * The two readings of the outbound leg, side by side. Null when there is no second source.
 *
 * WHY BOTH NUMBERS ARE RETURNED TOGETHER. The point of recording the derived completion is the
 * GAP: on SFax, `t_fax_sent` is `faxdateiso`, which equals the confirmation email's `Time` and is
 * therefore the provider's ACCEPTANCE. One real run submitted at 15:42:51 UTC had `faxdateiso` of
 * 15:43:02 — eleven seconds later — while its reported duration was 51 s, putting the actual
 * completion at about 15:43:53. So the leg reads 11 s for a transmission of about 62 s,
 * understated more than fivefold, and the bias FLATTERS the provider. A reader shown only one of
 * the two numbers cannot see any of that, which is why one of them alone is not a display of this
 * measurement.
 *
 * `scoredSeconds` IS the number that decides the outbound SLA verdict; `derivedSeconds` is not and
 * never becomes it (Decision 1, settled 2026-09-12). The caller is expected to say so on screen.
 *
 * DELIBERATELY NOT A MILESTONE. `t_fax_sent_derived` is absent from `MILESTONE_ORDER`, so no
 * adjacent-milestone pairing in `timelineSegments` can turn it into a slice of the bar — the same
 * structural separation `internalPhases` relies on, and for the same reason: a value that must not
 * enter a leg is kept out of the list legs are computed from, rather than being kept out by
 * everyone remembering to.
 */
export interface SecondSourceReading {
  /** The derived completion instant, as the API sent it. */
  at: string
  /** `t_fax_sent_derived − t_submitted`: the leg as the provider's own duration implies it. */
  derivedSeconds: number | null
  /** `t_fax_sent − t_submitted`: the leg as SCORED. This is the one a verdict uses. */
  scoredSeconds: number | null
  /**
   * How much longer the derived reading is than the scored one, in seconds.
   *
   * SIGNED, and a negative value is worth seeing rather than hiding: it would mean the derived
   * completion PRECEDES the scored one, which cannot happen if the scored value is an acceptance
   * time and would say the derivation is wrong on this row.
   */
  understatedBySeconds: number | null
}

export function outboundSecondSource(
  src: TransmissionMeasurementSource,
): SecondSourceReading | null {
  const at = src.t_fax_sent_derived
  if (!at || tsMillis(at) === null) return null
  const derivedSeconds = intervalSeconds(src.t_submitted, at)
  const scoredSeconds = intervalSeconds(src.t_submitted, src.t_fax_sent)
  const understatedBySeconds = intervalSeconds(src.t_fax_sent, at)
  return { at, derivedSeconds, scoredSeconds, understatedBySeconds }
}

/** The one em dash used for every absent measurement. */
export const DASH = '—'
/** Segment colours for the timeline. Distinct hues, all legible on the dark surface. */
export const SEGMENT_COLORS = ['#42a5f5', '#66bb6a', '#ffa726', '#ab47bc', '#26c6da', '#ec407a']

/** Parsed epoch ms, or null for absent AND for an unparseable string. */
export function tsMillis(ts?: string | null): number | null {
  if (!ts) return null
  const ms = new Date(ts).getTime()
  return Number.isFinite(ms) ? ms : null
}

/**
 * Seconds between two stamps, or null when either is absent or unparseable.
 *
 * SIGNED on purpose. A negative interval means the two clocks disagree about ordering —
 * eFax reporting completion after its own notification, say — and clamping it to 0 would
 * hide a real data problem behind a plausible-looking number.
 */
export function intervalSeconds(from?: string | null, to?: string | null): number | null {
  const a = tsMillis(from)
  const b = tsMillis(to)
  if (a === null || b === null) return null
  return (b - a) / 1000
}

/** A duration for display. null / undefined / non-finite all render as an em dash. */
export function fmtDuration(seconds?: number | null): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return DASH
  const abs = Math.abs(seconds)
  const sign = seconds < 0 ? '-' : ''
  if (abs < 1) return `${sign}${(abs * 1000).toFixed(0)} ms`
  if (abs < 60) return `${sign}${abs.toFixed(abs < 10 ? 2 : 1)} s`
  const mins = Math.floor(abs / 60)
  const rem = abs - mins * 60
  return `${sign}${mins}m ${rem.toFixed(0)}s`
}

/** A count for display. Distinguishes a real 0 from an absent value. */
export function fmtCount(n?: number | null): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH
  return String(n)
}

/**
 * "name (region / env)" for a pod, or null when there is no pod name.
 *
 * Returning null rather than a partial string is deliberate: region and env without a
 * pod name is not an attribution, and rendering "(usw2 / prod)" alone would look like
 * one.
 */
export function podLine(
  name?: string | null,
  region?: string | null,
  env?: string | null,
): string | null {
  if (!name) return null
  const qualifiers = [region, env].filter((v): v is string => !!v)
  return qualifiers.length ? `${name} (${qualifiers.join(' / ')})` : name
}

export type InboundTransport = 'eapi_webhook' | 'imap' | 'unknown'

/**
 * Which transport carried the inbound leg — which decides whether a blank AFC pod is
 * PERMANENT or merely missing.
 *
 * `inbound_method` wins when present. Otherwise the call is made on EVIDENCE only one
 * transport can produce: an IMAP Message-ID or headers, or a stored webhook payload.
 * Anything else is 'unknown' and is reported as such. Inferring 'eapi_webhook' from the
 * mere ABSENCE of IMAP evidence would stamp a permanent "not applicable" over a cell
 * that may simply be unpopulated — which is the one thing worse than a blank cell.
 */
export function inferInboundTransport(src: TransmissionMeasurementSource): InboundTransport {
  const declared = (src.inbound_method ?? '').trim()
  if (declared === 'imap' || declared === 'eapi_webhook') return declared
  if (src.imap_message_id || (src.imap_headers && Object.keys(src.imap_headers).length > 0)) {
    return 'imap'
  }
  if (src.webhook_payload_body || src.webhook_payload_headers) return 'eapi_webhook'
  return 'unknown'
}

/** How the AFC (inbound) pod cell should read when there is no pod name. */
export type AfcBlankReason = 'unobtainable_on_transport' | 'not_captured' | 'transport_unknown'

/**
 * Classifies a blank AFC pod. Present pod => null (nothing to explain).
 *
 * The three outcomes are three different facts and the UI must not merge them:
 * 'unobtainable_on_transport' is a final answer, 'not_captured' may yet be filled in,
 * and 'transport_unknown' means we cannot say which of the two applies.
 */
export function afcBlankReason(src: TransmissionMeasurementSource): AfcBlankReason | null {
  if (podLine(src.inbound_pod_name, src.inbound_pod_region, src.inbound_pod_env)) return null
  const transport = inferInboundTransport(src)
  if (transport === 'eapi_webhook') return 'unobtainable_on_transport'
  if (transport === 'imap') return 'not_captured'
  return 'transport_unknown'
}

export interface Milestone {
  key: string
  label: string
  at: string
  ms: number
  /** Why this clock exists and what it is not. Surfaced as the row's tooltip. */
  hint: string
}

/** The six clocks the timeline can draw, in declaration order with their captions. */
export const MILESTONE_ORDER = [
  't_submitted',
  't_fax_sent',
  't_efax_completed',
  't_efax_notified',
  't_notified',
  't_received',
] as const

/**
 * The milestones that actually have a value, in real chronological order.
 *
 * Sorted by TIMESTAMP rather than laid out in a hard-coded pipeline order. These clocks
 * come from three different systems (ours, eFax's reporting, our poller) and a fixed
 * order would draw a segment backwards without saying so. Sorting keeps the axis a true
 * left-to-right time axis, and a genuine ordering anomaly then shows up as a segment
 * between the two stamps that really are adjacent.
 */
export function milestones(src: TransmissionMeasurementSource): Milestone[] {
  const candidates: Array<{ key: string; label: string; at?: string | null; hint: string }> = [
    {
      key: 't_submitted',
      label: 'Submitted',
      at: src.t_submitted,
      hint: 'Our clock: the send was accepted by the provider.',
    },
    {
      key: 't_fax_sent',
      label: 'Fax sent (observed)',
      at: src.t_fax_sent,
      hint: 'Our clock: when WE observed the transmission complete, by status poll or notification.',
    },
    {
      key: 't_efax_completed',
      label: 'eFax completed (reported)',
      at: src.t_efax_completed,
      hint: "eFax's clock: when eFax says the fax finished. Not the same as our observation.",
    },
    {
      key: 't_efax_notified',
      label: 'eFax notified',
      at: src.t_efax_notified,
      hint: "eFax's clock: when eFax emitted the notification about the finished fax.",
    },
    {
      key: 't_notified',
      label: 'Notification landed',
      at: src.t_notified,
      hint: 'Our clock: when the notification actually reached us.',
    },
    {
      key: 't_received',
      label: 'Delivery (best estimate)',
      at: src.t_received,
      hint:
        'Best estimate of ACTUAL delivery. Deliberately NOT normalised to our polling ' +
        'clock — the IMAP poller wakes on an interval, and charging that lag to the SLA ' +
        'would bill us for latency the recipient never experienced.',
    },
  ]

  const present: Milestone[] = []
  for (const c of candidates) {
    const ms = tsMillis(c.at)
    if (ms === null || !c.at) continue
    present.push({ key: c.key, label: c.label, at: c.at, ms, hint: c.hint })
  }
  present.sort((a, b) => a.ms - b.ms)
  return present
}

export interface TimelineSegment {
  fromKey: string
  toKey: string
  label: string
  seconds: number
  /** Share of the total span, 0..1. A zero-length total yields 0, never NaN. */
  fraction: number
  color: string
}

/**
 * Proportional segments between chronologically adjacent milestones.
 *
 * Returns [] when fewer than two milestones are known: there is no such thing as a
 * one-point timeline, and stretching a bar across a single stamp would invent a
 * duration. The component renders explicit "not captured" text for that case.
 */
export function timelineSegments(src: TransmissionMeasurementSource): TimelineSegment[] {
  const points = milestones(src)
  if (points.length < 2) return []
  const total = points[points.length - 1].ms - points[0].ms
  const segments: TimelineSegment[] = []
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    const span = b.ms - a.ms
    segments.push({
      fromKey: a.key,
      toKey: b.key,
      label: `${a.label} → ${b.label}`,
      seconds: span / 1000,
      // total === 0 when every stamp shares a millisecond. Guarding it is what keeps
      // 0/0 out of a CSS width, where NaN silently collapses the entire bar.
      fraction: total > 0 ? span / total : 0,
      color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
    })
  }
  return segments
}

/**
 * Airtime per page, or null when it cannot be computed.
 *
 * `pages === 0` must NOT divide: that produces Infinity, which React renders as the
 * literal word "Infinity" in the cell.
 */
export function airtimePerPage(
  durationSeconds?: number | null,
  pages?: number | null,
): number | null {
  if (durationSeconds === null || durationSeconds === undefined) return null
  if (pages === null || pages === undefined) return null
  if (!Number.isFinite(durationSeconds) || !Number.isFinite(pages)) return null
  if (pages <= 0) return null
  return durationSeconds / pages
}

/** The intervals worth naming, each defined by ONE specific pair of clocks. */
export interface NamedIntervals {
  efaxNotifyLagSeconds: number | null
  networkQueueSeconds: number | null
  observationDelaySeconds: number | null
}

/**
 * The two intervals worth naming, plus our own observation delay.
 *
 * Each is computed from its SPECIFIC pair, never from whatever happened to be adjacent
 * on the sorted timeline — "eFax notify lag" is t_efax_notified − t_efax_completed and
 * nothing else, so it stays correct even when a third stamp falls between them.
 *
 * The return type is a NAMED interface rather than an inline object literal on purpose:
 * an inline `): { ... } {` gives the function two brace groups, and a structural test
 * looking for the body finds the type annotation instead and silently parses nothing.
 */
export function namedIntervals(src: TransmissionMeasurementSource): NamedIntervals {
  return {
    efaxNotifyLagSeconds: intervalSeconds(src.t_efax_completed, src.t_efax_notified),
    networkQueueSeconds: intervalSeconds(src.t_efax_notified, src.t_notified),
    observationDelaySeconds: intervalSeconds(src.t_received, src.t_notified),
  }
}
